import type { CSSProperties } from 'react'
import type { PurchasingVendorItemRow } from '@/services/purchasingTypes'
import type { FieldDefinition, FieldOption } from '@/tableControls'
import { readStoredColumnWidths, saveColumnWidths } from '@/tableControls'

export const PURCHASING_VENDOR_ITEMS_PAGE_KEY = 'purchasing-vendor-items'

export const PURCHASING_VENDOR_ITEMS_COLUMN_KEYS = [
  'qbo_vendor_id',
  'vendor_item_code',
  'description',
  'brand',
  'pack_text',
  'product_name',
  'match_status',
  'purchase_count',
  'last_seen_at',
] as const

export type PurchasingVendorItemsColumnKey = (typeof PURCHASING_VENDOR_ITEMS_COLUMN_KEYS)[number]

export const PURCHASING_VENDOR_ITEMS_DEFAULT_WIDTHS: Record<string, number> = {
  qbo_vendor_id: 140,
  vendor_item_code: 120,
  description: 220,
  brand: 120,
  pack_text: 140,
  product_name: 180,
  match_status: 120,
  purchase_count: 88,
  last_seen_at: 120,
}

export const PURCHASING_VENDOR_ITEMS_COLUMN_LABELS: Record<PurchasingVendorItemsColumnKey, string> = {
  qbo_vendor_id: 'Vendor',
  vendor_item_code: 'Item code',
  description: 'Description',
  brand: 'Brand',
  pack_text: 'Pack',
  product_name: 'Product',
  match_status: 'Match status',
  purchase_count: 'Purchases',
  last_seen_at: 'Last seen',
}

const SORTABLE_COLUMN_KEYS = new Set<PurchasingVendorItemsColumnKey>([
  'qbo_vendor_id',
  'vendor_item_code',
  'description',
  'brand',
  'pack_text',
  'product_name',
  'match_status',
  'purchase_count',
  'last_seen_at',
])

export const PURCHASING_VENDOR_ITEMS_DEFAULT_SORT = [
  { field: 'last_seen_at', direction: 'desc' as const },
]

export const PURCHASING_VENDOR_ITEMS_GROUP_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'qbo_vendor_id', label: 'Vendor' },
  { value: 'product_name', label: 'Product' },
  { value: 'match_status', label: 'Match status' },
]

export const MATCH_STATUS_FILTER_OPTIONS: FieldOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'auto', label: 'Auto-linked' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'pack_drift', label: 'Pack drift' },
]

export const PURCHASING_VENDOR_ITEMS_FIELD_DEFINITIONS: FieldDefinition[] =
  PURCHASING_VENDOR_ITEMS_COLUMN_KEYS.map((key) => {
    const type =
      key === 'purchase_count'
        ? 'number'
        : key === 'last_seen_at'
          ? 'date'
          : key === 'match_status'
            ? 'select'
            : 'text'
    const accessor = (row: unknown) => {
      const item = row as PurchasingVendorItemRow
      if (key === 'product_name') {
        return item.product_name ?? ''
      }
      return item[key as keyof PurchasingVendorItemRow]
    }
    return {
      key,
      label: PURCHASING_VENDOR_ITEMS_COLUMN_LABELS[key],
      type,
      defaultVisible: true,
      sortable: SORTABLE_COLUMN_KEYS.has(key),
      accessor,
      ...(key === 'match_status' ? { options: MATCH_STATUS_FILTER_OPTIONS } : {}),
    }
  })

export function fixedColumnStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
  }
}

export function isPurchasingVendorItemsColumnSortable(key: string): boolean {
  return SORTABLE_COLUMN_KEYS.has(key as PurchasingVendorItemsColumnKey)
}

export function buildPurchasingVendorItemsFilterFields(
  vendorOptions: FieldOption[],
  productOptions: FieldOption[],
): FieldDefinition[] {
  return [
    {
      key: 'qbo_vendor_id',
      label: 'Vendor',
      type: 'multiSelect',
      filterable: true,
      options: vendorOptions,
      accessor: (row) => (row as PurchasingVendorItemRow).qbo_vendor_id,
    },
    {
      key: 'product_id',
      label: 'Product',
      type: 'multiSelect',
      filterable: true,
      options: productOptions,
      accessor: (row) => String((row as PurchasingVendorItemRow).product_id ?? ''),
    },
    {
      key: 'match_status',
      label: 'Match status',
      type: 'multiSelect',
      filterable: true,
      options: MATCH_STATUS_FILTER_OPTIONS,
      accessor: (row) => (row as PurchasingVendorItemRow).match_status,
    },
  ]
}

export function buildPurchasingVendorItemsExportMatrix(rows: PurchasingVendorItemRow[]): {
  headers: string[]
  rows: string[][]
} {
  const headers = PURCHASING_VENDOR_ITEMS_COLUMN_KEYS.map(
    (key) => PURCHASING_VENDOR_ITEMS_COLUMN_LABELS[key],
  )
  const exportRows = rows.map((row) =>
    PURCHASING_VENDOR_ITEMS_COLUMN_KEYS.map((key) => {
      if (key === 'product_name') {
        return row.product_name ?? ''
      }
      const value = row[key as keyof PurchasingVendorItemRow]
      return value == null ? '' : String(value)
    }),
  )
  return { headers, rows: exportRows }
}

export function migrateLegacyPurchasingVendorItemsColumnWidths(): void {
  const existing = readStoredColumnWidths(PURCHASING_VENDOR_ITEMS_PAGE_KEY)
  if (Object.keys(existing).length > 0) return
  const legacyKey = 'bookkeeper:purchasing-vendor-items-widths'
  try {
    const raw = localStorage.getItem(legacyKey)
    if (!raw) return
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const widths: Record<string, number> = {}
    for (const key of PURCHASING_VENDOR_ITEMS_COLUMN_KEYS) {
      const value = parsed[key]
      if (typeof value === 'number' && Number.isFinite(value)) {
        widths[key] = value
      }
    }
    if (Object.keys(widths).length > 0) {
      saveColumnWidths(PURCHASING_VENDOR_ITEMS_PAGE_KEY, widths)
    }
  } catch {
    /* ignore */
  }
}
